import { Plus, ShieldX, Sparkles, X } from "lucide-react";
import { Badge } from "@sphinx/design-system/badge";
import { Button } from "@sphinx/design-system/button";
import { Input } from "@sphinx/design-system/input";
import { Label } from "@sphinx/design-system/label";
import { Textarea } from "@sphinx/design-system/textarea";
import { ExpectedToolsEditor } from "../expected-tools-editor";
import { cn } from "@/lib/utils";
import { YARN_ORGS, type YarnOrg } from "@/shared/sphinx";
import type {
  AvailableTool,
  TestTemplate,
} from "@/components/evals/eval-runner/types";

interface TestsStepProps {
  testTemplates: TestTemplate[];
  availableTools: AvailableTool[];
  canGenerateTests: boolean;
  hasServerAndModelSelection: boolean;
  isGenerating: boolean;
  isGeneratingNegativeTests: boolean;
  onGenerateTests: () => void;
  onGenerateNegativeTests: () => void;
  onAddTestTemplate: () => void;
  onAddNegativeTestTemplate: () => void;
  onRemoveTestTemplate: (index: number) => void;
  onUpdateTestTemplate: <K extends keyof TestTemplate>(
    index: number,
    field: K,
    value: TestTemplate[K],
  ) => void;
}

/**
 * Per-case org pins: the run expands the case once per selected org; none
 * selected = the run's default org (Review step). Multi-select chips.
 */
function CaseOrgsPicker({
  value,
  onChange,
}: {
  value: YarnOrg[] | undefined;
  onChange: (orgs: YarnOrg[]) => void;
}) {
  const selected = new Set(value ?? []);
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase text-muted-foreground">Orgs</Label>
      <div className="flex flex-wrap items-center gap-1.5" data-testid="case-orgs-picker">
        {YARN_ORGS.map((org) => {
          const on = selected.has(org);
          return (
            <button
              key={org}
              type="button"
              aria-pressed={on}
              className={cn(
                "rounded-full border px-2.5 py-0.5 text-xs transition-colors",
                on
                  ? "border-primary bg-primary/10 text-foreground"
                  : "bg-muted/40 text-muted-foreground hover:text-foreground",
              )}
              onClick={() =>
                onChange(
                  on
                    ? [...selected].filter((o) => o !== org)
                    : [...selected, org],
                )
              }
            >
              {org}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">
        {selected.size > 0
          ? `Runs once per selected org (${selected.size}).`
          : "None selected — uses the run's default org."}
      </p>
    </div>
  );
}

export function TestsStep({
  testTemplates,
  availableTools,
  canGenerateTests,
  hasServerAndModelSelection,
  isGenerating,
  isGeneratingNegativeTests,
  onGenerateTests,
  onGenerateNegativeTests,
  onAddTestTemplate,
  onAddNegativeTestTemplate,
  onRemoveTestTemplate,
  onUpdateTestTemplate,
}: TestsStepProps) {
  const hasExistingContent =
    testTemplates.length > 0 &&
    testTemplates.some((template) => template.query.trim().length > 0);

  const renderPositiveTestCard = (template: TestTemplate, index: number) => (
    <div key={index} className="space-y-3 rounded-lg border bg-background p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col flex-1 space-y-2">
          <Label className="text-xs uppercase text-muted-foreground">
            Title
          </Label>
          <Input
            className="w-full"
            value={template.title}
            onChange={(event) =>
              onUpdateTestTemplate(index, "title", event.target.value)
            }
            placeholder="(Paypal) List transactions"
          />
        </div>
        {testTemplates.length > 1 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onRemoveTestTemplate(index)}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {template.promptTurns && template.promptTurns.length > 1 ? (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="outline">{template.promptTurns.length} turns</Badge>
          <span>
            Additional follow-up turns are preserved and will be available in
            the test case editor after creation.
          </span>
        </div>
      ) : null}

      <div className="space-y-2">
        <Label className="text-xs uppercase text-muted-foreground">Query</Label>
        <Textarea
          value={template.query}
          onChange={(event) =>
            onUpdateTestTemplate(index, "query", event.target.value)
          }
          placeholder="Can you find the most recent Paypal transactions, then create an invoice?"
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase text-muted-foreground">Runs</Label>
        <Input
          type="number"
          min={1}
          value={template.runs}
          onChange={(event) =>
            onUpdateTestTemplate(
              index,
              "runs",
              Number(event.target.value) > 0 ? Number(event.target.value) : 1,
            )
          }
        />
      </div>

      <CaseOrgsPicker
        value={template.yarnOrgs}
        onChange={(orgs) => onUpdateTestTemplate(index, "yarnOrgs", orgs)}
      />

      <div className="space-y-2">
        <Label className="text-xs uppercase text-muted-foreground">
          Expected tools
        </Label>
        <ExpectedToolsEditor
          toolCalls={template.expectedToolCalls}
          onChange={(toolCalls) =>
            onUpdateTestTemplate(index, "expectedToolCalls", toolCalls)
          }
          availableTools={availableTools}
        />
      </div>
    </div>
  );

  const renderNegativeTestCard = (template: TestTemplate, index: number) => (
    <div
      key={index}
      className="space-y-3 rounded-lg border border-info/50 bg-info/50 p-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className="bg-info/50 text-foreground border-info/50"
          >
            <ShieldX className="h-3 w-3 mr-1" />
            Negative Test
          </Badge>
        </div>
        {testTemplates.length > 1 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onRemoveTestTemplate(index)}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="flex flex-col flex-1 space-y-2">
        <Label className="text-xs uppercase text-muted-foreground">Title</Label>
        <Input
          className="w-full"
          value={template.title}
          onChange={(event) =>
            onUpdateTestTemplate(index, "title", event.target.value)
          }
          placeholder="Meta question about search"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase text-muted-foreground">
          Scenario
        </Label>
        <p className="text-xs text-muted-foreground mb-1">
          Describe why the AI should NOT trigger any tools for this prompt
        </p>
        <Textarea
          value={template.scenario || ""}
          onChange={(event) =>
            onUpdateTestTemplate(index, "scenario", event.target.value)
          }
          placeholder="User is asking about how the search feature works, not performing a search"
          rows={2}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase text-muted-foreground">
          User Prompt
        </Label>
        <p className="text-xs text-muted-foreground mb-1">
          The prompt that should NOT trigger any tools
        </p>
        <Textarea
          value={template.query}
          onChange={(event) =>
            onUpdateTestTemplate(index, "query", event.target.value)
          }
          placeholder="Can you explain what parameters the search tool accepts?"
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase text-muted-foreground">Runs</Label>
        <Input
          type="number"
          min={1}
          value={template.runs}
          onChange={(event) =>
            onUpdateTestTemplate(
              index,
              "runs",
              Number(event.target.value) > 0 ? Number(event.target.value) : 1,
            )
          }
        />
      </div>

      <CaseOrgsPicker
        value={template.yarnOrgs}
        onChange={(orgs) => onUpdateTestTemplate(index, "yarnOrgs", orgs)}
      />
    </div>
  );

  const renderTemplates = () => (
    <div className="space-y-12">
      {testTemplates.map((template, index) =>
        template.isNegativeTest
          ? renderNegativeTestCard(template, index)
          : renderPositiveTestCard(template, index),
      )}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onAddTestTemplate}
          aria-label="Add test case"
          className="flex-1"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add test case
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onAddNegativeTestTemplate}
          aria-label="Add negative test case"
          className="flex-1 border-info/50 text-info hover:bg-info/50 hover:text-foreground"
        >
          <ShieldX className="h-4 w-4 mr-2" />
          Add negative test
        </Button>
      </div>
    </div>
  );

  const shouldShowLockedState =
    !hasServerAndModelSelection && !hasExistingContent;

  if (shouldShowLockedState) {
    return (
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg pb-2">Define your test cases</h3>
            <p className="text-sm text-muted-foreground">
              Specify tool names and their expected arguments. Leave arguments
              empty to skip argument checking.
            </p>
          </div>
        </div>
        <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
          <p className="font-medium text-foreground">
            Finish previous steps first
          </p>
          <p className="mt-2">
            Select at least one server and choose a model to unlock test
            authoring. That ensures generated tests know which stack to target.
          </p>
        </div>
      </div>
    );
  }

  const isAnyGenerating = isGenerating || isGeneratingNegativeTests;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg">Define your test cases</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Specify tool names and their expected arguments. Leave arguments
            empty to skip argument checking.
          </p>
        </div>
        {canGenerateTests && (
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onGenerateTests}
              disabled={isAnyGenerating}
            >
              {isGenerating ? "Generating..." : "Generate tests"}
              <Sparkles className="h-4 w-4 ml-2" />
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onGenerateNegativeTests}
              disabled={isAnyGenerating}
              className="border-info/50 text-info hover:bg-info/50 hover:text-foreground"
            >
              {isGeneratingNegativeTests
                ? "Generating..."
                : "Generate negative tests"}
              <ShieldX className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}
      </div>

      {isAnyGenerating && hasServerAndModelSelection ? (
        <div className="flex items-center justify-center rounded-lg border p-6">
          <div className="text-center text-sm text-muted-foreground">
            {isGenerating
              ? "Generating test cases..."
              : "Generating negative test cases..."}
          </div>
        </div>
      ) : (
        renderTemplates()
      )}
    </div>
  );
}
